import { z } from "zod";

/**
 * GEREKÇE — geri alınamaz işlemlerin denetim kaydına yazdığı metin
 *
 * ================================================================
 * NEDEN BURADA: `min(3)` BOŞLUĞU KABUL EDİYORDU
 * ================================================================
 * 04.09.2026'da canlı ölçüldü: `gerekce: "   "` (üç boşluk)
 * `z.string().min(3)` doğrulamasından **geçiyor** ve doğrudan denetim
 * kaydına yazılıyordu. Yani KVKK kapsamındaki geri alınamaz bir işlem
 * — dosya silme, veli bağlantısı kaldırma — **gerekçesi boş** bir
 * denetim kaydı üretebiliyordu.
 *
 * Kural on dört ayrı denetleyicide tek tek yazılmıştı ve **yalnızca
 * biri** (`auth.controller.ts`, cam kırma gerekçesi) `.trim()`
 * yazmıştı. Yani kural doğru biliniyordu; tekrar edildiği için
 * kaybolmuştu.
 *
 * CLAUDE.md kural 10: *"Koruma, unutulabilecek bir yere değil işin
 * kendisine bağlanır."* Gerekçe metninin kendisi bu şema; boşluğu
 * eleme kararı artık uçların değil, sözleşmenin.
 *
 * Arayüz de `.trim().length` ile engelliyordu — ama arayüz koruma
 * değil. İstek doğrudan uca atılabiliyor ve öyle atıldığında geçiyordu.
 */

/** Sıradan gerekçe alt sınırı. */
export const GEREKCE_EN_AZ = 3;

/**
 * Ağır işlemlerin alt sınırı (cam kırma, rol/izin değişikliği).
 *
 * Keyfi değil: "test" gibi bir gerekçe denetimde hiçbir şey anlatmıyor
 * ve KVKK incelemesinde sorulacak soru tam da "neden bu kayda
 * baktınız" oluyor.
 */
export const GEREKCE_AGIR_EN_AZ = 10;

/**
 * KORUMALI PDR NOTU okuma gerekçesi — en yüksek alt sınır.
 *
 * ================================================================
 * BU SAYI ÜÇ YERDE AYRI AYRI YAZILIYDI
 * ================================================================
 * 04.09.2026'da ölçüldü: `gorusme.service.ts` 15 diyordu,
 * `apps/panel/src/api.ts` kendi `GEREKCE_EN_AZ = 15`ini tanımlıyordu,
 * ve denetleyici şeması `enAz: 1` ile geçiyordu.
 *
 * Panelin sabiti sözleşmedeki `GEREKCE_EN_AZ`ı (3) **gölgeliyordu**:
 * aynı depoda aynı ad, iki farklı sayı, hangisini aldığın import
 * yoluna bağlı ve ikisi de doğru görünüyor. Bağımsız denetim yarım
 * saatini buna kaybetti — testi sözleşmeden aldı, ekran 15
 * kullanıyordu, hata mesajı sebebi söylemedi.
 *
 * Ad artık gölgelenemeyecek kadar açık ve sayı TEK yerde.
 *
 * Sınır neden bu kadar yüksek: burada okunanı bir çocuğun psikolojik
 * danışmanlık notu. "x" ya da "baktım" gibi bir gerekçe, KVKK
 * incelemesinde sorulacak "neden bu notu açtınız" sorusunu
 * cevaplamıyor — ve o cevap sonradan hatırlanmıyor.
 */
export const KORUMALI_NOT_GEREKCE_EN_AZ = 15;

export const GEREKCE_EN_COK = 1000;

export interface GerekceSecenegi {
  enAz?: number;
  enCok?: number;
  /** Kullanıcıya görünen mesaj — uç kendi bağlamını anlatır. */
  mesaj?: string;
}

/**
 * Gerekçe şeması. `.trim()` doğrulamadan ÖNCE çalışır, yani boşluktan
 * ibaret bir metin alt sınırı geçemez; servise de kırpılmış hâli
 * ulaşır, dolayısıyla denetim kaydına baştaki/sondaki boşluk yazılmaz.
 */
export function gerekceSemasi(secenek: GerekceSecenegi = {}): z.ZodString {
  const enAz = secenek.enAz ?? GEREKCE_EN_AZ;
  const enCok = secenek.enCok ?? GEREKCE_EN_COK;
  return z
    .string()
    .trim()
    .min(enAz, secenek.mesaj ?? `Gerekçe en az ${enAz} karakter olmalı.`)
    .max(enCok, "Gerekçe çok uzun.");
}

/**
 * İSTEĞE BAĞLI gerekçe.
 *
 * Boş dizgi ile "hiç yazılmamış" AYNI ŞEY sayılıyor: `""` ve `"   "`
 * `null`'a çevriliyor. Aksi halde denetim kaydında `reason: ""` ile
 * `reason: null` iki ayrı hâl gibi görünür, oysa ikisi de "gerekçe
 * yok" demek.
 */
export function istegeBagliGerekceSemasi(
  secenek: GerekceSecenegi = {},
): z.ZodEffects<z.ZodNullable<z.ZodOptional<z.ZodString>>, string | null | undefined> {
  const enCok = secenek.enCok ?? GEREKCE_EN_COK;
  return z
    .string()
    .max(enCok, "Gerekçe çok uzun.")
    .optional()
    .nullable()
    .transform((s) => {
      if (s === undefined || s === null) return s;
      const k = s.trim();
      return k.length === 0 ? null : k;
    });
}
